import { User, WindowAccess } from "../models/user/User";
import { UserSettings } from "../models/user/UserSettings";
import { get, set, remove } from "./Preferences";
import { getUserSessionSettings } from "../api/UserSetups";
import { getWindowsAccessForPlant } from "../api/SecurityWindowAccess";

const _userSettingsKey = "UserSettings";
const _userKey = "UserInfo";

// In-memory cache to avoid repeated reads from Capacitor Preferences
// In-memory Promise cache to avoid repeated reads from Capacitor Preferences.
// Caching the Promise (not the resolved value) ensures concurrency safety:
// concurrent callers share the same in-flight read instead of each triggering one.
let _userPromise: Promise<User | null> | null = null;
let _userSettingsPromise: Promise<UserSettings | null> | null = null;

const getCachedUser = (): Promise<User | null> => {
  if (_userPromise == null) {
    _userPromise = get(_userKey).then((u) => (u as User) ?? null);
  }
  return _userPromise;
};

const getCachedUserSettings = (): Promise<UserSettings | null> => {
  if (_userSettingsPromise == null) {
    _userSettingsPromise = get(_userSettingsKey).then(
      (s) => (s as UserSettings) ?? null
    );
  }
  return _userSettingsPromise;
};

export const getUserId = async () => {
  const user = await getUser();
  if (user !== null) {
    return user.Id;
  }
  return null;
};

export const getUserDisplayName = async () => {
  const user = await getUser();
  if (user !== null) {
    return user.DisplayName;
  }
  return null;
};

export const getUserDatabaseId = async () => {
  const userSettings = await getCachedUserSettings();
  return userSettings?.SessionSettingList?.Database ?? null;
};

export const getUserPlantId = async () => {
  const userSettings = await getCachedUserSettings();
  return userSettings?.SessionSettingList?.Plant ?? null;
};

// US 874329: whether the current session's plant is a test/training environment.
export const getIsTestEnvironment = async () => {
  const userSettings = await getCachedUserSettings();
  return userSettings?.SessionSettingList?.IsTestEnvironment ?? false;
};

export const getUserTechnicianAccess = async () => {
  return getUserHasAccess("mob_technician", "Update");
};

export const getUserRequesterAccess = async () => {
  return getUserHasAccess("mob_requester", "Update");
};

export const getStoresAccess = async () => {
  return getUserHasAccess("mob_stores", "Update");
};

export const getEmployeesAccess = async () => {
  return getUserHasAccess(
    "w_wo_worker_time_eu",
    "Has access to other employee ids"
  );
};

export const getCraftCodeAccess = async () => {
  return getUserHasAccess(
    "w_wo_worker_time_eu",
    "Has access to all craft codes"
  );
};

export const getRemarksAccess = async () => {
  return getUserHasAccess("mob_remarks", "Remarks");
};

export const getTimeEntryAccess = async () => {
  return getUserHasAccess("mob_worker_time_reporting", "New");
};

export const getMeterReadingAccess = async () => {
  return getUserHasAccess("mob_eq_meter_readings", "New");
};

export const getInspectionAccess = async () => {
  return getUserHasAccess("mob_inspections", "Update");
};

export const getScheduleAccess = async () => {
  return getUserHasAccess("mob_schedule", "Schedule");
};

export const getExecuteInspectionAccess = async () => {
  return getUserHasAccess("w_eq_inspection", "Capture Results");
};

export const getCreateWorkOrderAccess = async () => {
  const hasEmergencyNew = await getUserHasAccess(
    "w_wo_book_eu_main",
    "Work Order Tab Emergency New"
  );
  const hasRoutineNew = await getUserHasAccess(
    "w_wo_book_eu_main",
    "Work Order Tab Routine New"
  );
  return hasEmergencyNew || hasRoutineNew;
};

export const getOpenWorkOrdersAccess = async () => {
  return getUserHasAccess("w_wo_search", "Inquiry");
};

export const getCCAAccess = async () => {
  return getUserHasAccess("mob_cca_codes", "CCA");
};

export const getIssueFromTechAccess = async () => {
  return getUserHasAccess("mob_in_issues", "Issue Material");
};

export const getReturnFromTechAccess = async () => {
  return getUserHasAccess("mob_in_return", "Return Material");
};

export const getAssignTechAccess = async () => {
  const userInfo = await getCachedUser();
  const woMassUpdateActions = [
    "Routine Update",
    "Emergency Update",
    "PM-Work Order Update",
    "PM-Master Plan Update",
    "Standard Request Update",
  ];
  return (
    userInfo?.UserAccess?.some(
      (ua) =>
        ua.WindowName === "w_wo_mass_update" &&
        woMassUpdateActions.includes(ua.Action) &&
        ua.HasAccess
    ) ?? false
  );
};

export const getIssueAccess = async () => {
  return getUserHasAccess("mob_stores_issue", "Issue Material");
};

export const getReturnAccess = async () => {
  return getUserHasAccess("mob_stores_return", "Return Material");
};

export const getCycleCountAccess = async () => {
  return getUserHasAccess("mob_stores_cycle_count", "Cycle Count");
};

export const getBinAccess = async () => {
  return getUserHasAccess("mob_stores_bintobin", "Bin to Bin Transfer");
};
export const getInventoryDocumentAccess = async () => {
  return getUserHasAccess("w_in_item_book_eu_main", "Documents Tab View");
};
export const getEquipmentDocumentAccess = async () => {
  return getUserHasAccess("w_eq_location_eu", "Documents Tab View");
};

export const getItemAdjustAccess = async () => {
  return getUserHasAccess("mob_stores_itemadjust", "Item Adjustment");
};

export const getPOReceiptsAccess = async () => {
  return getUserHasAccess("mob_po_receipts", "PO Receipts");
};

export const getApprovalAccess = async () => {
  return getUserHasAccess("mob_wo_approvals", "Update");
};

export const getMobReqApprovalAccess = async () => {
  return getUserHasAccess("mob_req_approvals", "Update");
};

// EPIC - 581384
export const getQuickWorkOrderEmergencyAccess = async () => {
  return getUserHasAccess("w_wo_quick_wo", "Emergency New");
};

export const getQuickWorkOrderRoutineAccess = async () => {
  return getUserHasAccess("w_wo_quick_wo", "Routine New");
};

export const getEquipmentHierarchyInquiryAccess = async () => {
  return getUserHasAccess("w_eq_hierarchy", "Inquiry");
};

export const getCommentsInquiryAccess = async () => {
  return getUserHasAccess("w_wo_book_eu_main", "Comments Inquiry");
};

export const getCommentsUpdateAccess = async () => {
  return getUserHasAccess("w_wo_book_eu_main", "Comments Update");
};

export const getStatusChangeAccess = async () => {
  return getUserHasAccess("w_wo_status_eu", "Change Status 70-78");
};

export const getUserHasAccess = async (windowName: string, action: string) => {
  const userInfo = await getCachedUser();
  return (
    userInfo?.UserAccess?.find((ua) => {
      return ua.WindowName === windowName && ua.Action === action;
    })?.HasAccess ?? false
  );
};

export const getUserSettings = async () => {
  return (await getCachedUserSettings()) as UserSettings;
};

export const setUserSettings = async (userSettings: UserSettings) => {
  _userSettingsPromise = null;
  await remove(_userSettingsKey);
  await set(_userSettingsKey, userSettings);
};

export const getUser = async () => {
  return await getCachedUser();
};

export const setUser = async (user: User) => {
  _userPromise = Promise.resolve(user);
  await remove(_userKey);
  await set(_userKey, user);
};

export const loadUserSettings = async () => {
  const response = await getUserSessionSettings();
  if (response.status === 200) {
    await createUserSettings(response.data);
    return true;
  }
  console.log("GetUserSessionSettings error: ", response.data);
  return false;
};

// US 874329: normalize the IsTestEnvironment flag from the session settings
// response. Backward compatible with backends that predate the field:
//  - undefined/null (field absent)  -> false (banner off, no UI break)
//  - "Y"/"y" (current server format) -> true
//  - a raw boolean (future-proofing) -> the boolean itself
// Anything else resolves to false.
const parseTestEnvironmentFlag = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.trim().toUpperCase() === "Y";
  return false;
};

const createUserSettings = async (responseData: any) => {
  const userSettings: UserSettings = {
    DocumentProviders: [],
    SessionSettingList: {
      Database: responseData.SessionSettingsList.Database,
      EmployeeEmailAddress:
        responseData.SessionSettingsList.EmployeeEmailAddress,
      MaxsizeForDocumentUpload:
        responseData.SessionSettingsList.MaxsizeForDocumentUpload,
      Plant: responseData.SessionSettingsList.Plant,
      UserDisplayName: responseData.SessionSettingsList.UserDisplayName,
      UserId: responseData.SessionSettingsList.UserId,
      CloudSuiteManaged: responseData.SessionSettingsList.CloudSuiteManaged,
      // US 874329: server sends "Y"/"N" (mirroring EAM web IsTestEnvironment).
      // Backward compatible: an older backend that predates this field just
      // omits it, which safely resolves to false (banner off) — the UI never
      // breaks if this one variable is missing.
      IsTestEnvironment: parseTestEnvironmentFlag(
        responseData?.SessionSettingsList?.IsTestEnvironment
      ),
    },
    UserOptionsList: {
      SessionTimeout: responseData.UserOptionsList.SessionTimeout,
      EnableItemAdjFromIssue:
        responseData.UserOptionsList.EnableItemAdjFromIssue,
    },
    ModulesInstalled: {
      MultipleWarehousing: responseData.ModulesInstalled.MultipleWarehousing,
    },
  };
  //update doc providers
  responseData.DocProvidersList.forEach((docProvider: any) => {
    userSettings.DocumentProviders.push({
      ApplicationId: docProvider.ApplicationId,
      Authority: docProvider.Authority,
      Enabled: docProvider.Enabled,
      Name: docProvider.Name,
      Plant: docProvider.Plant,
      Providerid: docProvider.Providerid,
      Secret: docProvider.Secret,
      Type: docProvider.Type,
      Url: docProvider.Url,
    });
  });
  //store user settings
  await setUserSettings(userSettings);
  //create user object and store
  await createUserInfo(userSettings);
};

const createUserInfo = async (userSettings: UserSettings) => {
  const userId = userSettings.SessionSettingList?.UserId;
  const plant = userSettings.SessionSettingList?.Plant;
  let ua: WindowAccess[] = [];

  if (!!userId && !!plant) {
    const res = await getWindowsAccessForPlant(userId, plant);
    if (!res.isError && res.data) {
      ua = res.data;
    }
  }
  const user: User = {
    Id: userId!,
    DisplayName: userSettings.SessionSettingList?.UserDisplayName!,
    EmployeeEmailAddress:
      userSettings.SessionSettingList?.EmployeeEmailAddress!,
    UserAccess: ua,
  };
  //store user
  await setUser(user);
};
